/*
  Ambient light (1 October).

  Lays a layer of slow lights into the cards that carry a holographic or a
  dark ground, the footer, and anything marked data-sa-ambient="<kind>":
  large colour fields that wander over the gradient, smaller lanterns that
  circle and breathe, and on the bright foil cards and the pack scenes a few
  glints that come and go. The layer sits behind the card's own content and
  takes no clicks. The look lives in sa-ambient.css; this file only places the
  lights, each card with its own fixed arrangement so the same card looks the
  same on every visit.

  Nothing animates off screen: an observer marks the layers and the pack
  scenes that are in view, and the stylesheet pauses the rest. With
  "Ambient light" off in Theme settings the theme does not load this file.
*/
(function () {
  if (window.__saAmbient) return;
  window.__saAmbient = true;

  var phone = window.matchMedia('(max-width: 749px)').matches;
  var HOSTS = [
    ['.sa-card--holo', 'bold'],
    ['.sa-card--dark', 'dark'],
    ['.sa-lead--soft, .sa-intro__card', 'soft'],
    ['footer.footer', 'night'],
    ['[data-sa-ambient]', '']
  ];
  // washes: the big colour fields; lamps: the lanterns; glints: the stars,
  // with the share of the width they may appear in
  var KIT = {
    bold: { washes: 3, lamps: 5, glints: 7, gx: [54, 96] },
    dark: { washes: 3, lamps: 5, glints: 0 },
    soft: { washes: 3, lamps: 4, glints: 0 },
    night: { washes: 2, lamps: 6, glints: 0 },
    scene: { washes: 0, lamps: 4, glints: 9, gx: [8, 92] }
  };

  // a small fixed sequence per card, so the arrangement does not change
  function seq(seed) {
    var s = (seed * 9301 + 49297) % 233280 || 7;
    return function () {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
  }
  function between(r, a, b) { return a + (b - a) * r(); }

  function orb(type, r, kind) {
    var wash = type === 'wash';
    var size = wash ? between(r, 52, 84) : between(r, kind === 'night' ? 10 : 8, kind === 'night' ? 24 : 19);
    var style = [
      '--x:' + between(r, wash ? 12 : 4, wash ? 88 : 96).toFixed(1) + '%',
      '--y:' + between(r, wash ? 15 : 8, wash ? 85 : 92).toFixed(1) + '%',
      '--s:' + size.toFixed(1) + '%',
      '--r:' + between(r, wash ? 18 : 30, wash ? 42 : 90).toFixed(0) + '%',
      '--od:' + between(r, wash ? 80 : 38, wash ? 140 : 84).toFixed(0) + 's',
      '--odir:' + (r() > 0.5 ? 'normal' : 'reverse'),
      '--bd:' + between(r, 7, 14).toFixed(1) + 's',
      '--dl:-' + between(r, 0, 60).toFixed(1) + 's'
    ].join(';');
    return '<span class="sa-amb__orbit sa-amb__orbit--' + type + '" style="' + style + '"><span class="sa-amb__orb"></span></span>';
  }

  function glint(r, gx) {
    var style = [
      '--x:' + between(r, gx[0], gx[1]).toFixed(1) + '%',
      '--y:' + between(r, 12, 88).toFixed(1) + '%',
      '--g:' + between(r, 10, 22).toFixed(0) + 'px',
      '--t:' + between(r, 3.6, 7.2).toFixed(1) + 's',
      '--dl:-' + between(r, 0, 7).toFixed(1) + 's'
    ].join(';');
    return '<span class="sa-amb__glint" style="' + style + '"></span>';
  }

  var io = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.target.classList.toggle('is-on', e.isIntersecting); });
      }, { rootMargin: '120px 0px' })
    : null;
  function watch(el) {
    if (io) io.observe(el);
    else el.classList.add('is-on');
  }

  var count = 0;
  function build(host, kind) {
    if (host.__saAmb) return;
    var kit = KIT[kind];
    if (!kit) return;
    host.__saAmb = true;
    count += 1;
    var r = seq(count * 131 + kind.length * 17);
    var washes = phone ? Math.min(kit.washes, 2) : kit.washes;
    var lamps = phone ? Math.max(3, kit.lamps - 2) : kit.lamps;
    var glints = phone ? Math.ceil(kit.glints / 2) : kit.glints;
    var html = '';
    var i;
    for (i = 0; i < washes; i++) html += orb('wash', r, kind);
    for (i = 0; i < lamps; i++) html += orb('lamp', r, kind);
    for (i = 0; i < glints; i++) html += glint(r, kit.gx);
    var layer = document.createElement('span');
    layer.className = 'sa-amb sa-amb--' + kind;
    layer.setAttribute('aria-hidden', 'true');
    layer.innerHTML = html;
    if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.classList.add('sa-amb-host');
    host.insertBefore(layer, host.firstChild);
    window.requestAnimationFrame(function () { layer.classList.add('is-ready'); });
    watch(layer);
  }

  function run(root) {
    HOSTS.forEach(function (pair) {
      (root || document).querySelectorAll(pair[0]).forEach(function (host) {
        build(host, pair[1] || host.getAttribute('data-sa-ambient'));
      });
    });
    (root || document).querySelectorAll('[data-sa-packs]').forEach(function (scene) {
      if (scene.__saWatched) return;
      scene.__saWatched = true;
      watch(scene);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { run(); });
  else run();
  // the theme editor redraws a section in place
  document.addEventListener('shopify:section:load', function (e) { run(e.target); });
})();
